import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES, papelDeAcesso } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, fetchAllPaginated, comCidadeAtiva, API_BASE_URL } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';
import SituacaoGradePainel from '../components/SituacaoGradePainel';

/**
 * Acompanhamento de Diários — visão da coordenação.
 *
 * Responde, dia a dia, à pergunta "qual aula não foi preenchida?".
 * A referência do que era esperado é a grade horária da turma; o que foi
 * feito vem do diário (aula registrada) e da frequência (chamada lançada).
 *
 * Estados de uma aula (vindos de /diary-coverage/):
 *   ok             → aula registrada e chamada lançada
 *   sem_frequencia → aula registrada, falta a chamada
 *   pendente       → nada registrado
 *   substituicao   → o horário foi cumprido por outra disciplina (permuta)
 *   nao_realizada  → a aula não aconteceu, e há motivo registrado
 *   extra          → aula fora da grade (reposição / aula extra)
 *   futura         → data ainda não chegou (não é cobrança)
 *
 * Dias marcados no calendário letivo não geram aula esperada nenhuma.
 */

const STATUS_META = {
  ok: { label: 'Completo', short: 'OK', dot: 'bg-[#13983D]', text: 'text-[#13983D]', soft: 'bg-[#13983D]/10' },
  substituicao: { label: 'Substituição', short: 'SU', dot: 'bg-[#7C3AED]', text: 'text-[#7C3AED]', soft: 'bg-[#7C3AED]/10' },
  sem_frequencia: { label: 'Sem frequência', short: 'SF', dot: 'bg-[#F9B047]', text: 'text-[#F9B047]', soft: 'bg-[#F9B047]/10' },
  pendente: { label: 'Não registrada', short: '!', dot: 'bg-[#E83A3C]', text: 'text-[#E83A3C]', soft: 'bg-[#E83A3C]/10' },
  nao_realizada: { label: 'Não realizada', short: 'NR', dot: 'bg-slate-500', text: 'text-slate-500', soft: 'bg-slate-500/10' },
  extra: { label: 'Aula extra', short: 'EX', dot: 'bg-[#0974BA]', text: 'text-[#0974BA]', soft: 'bg-[#0974BA]/10' },
  futura: { label: 'Aula futura', short: '·', dot: 'bg-slate-400', text: 'text-slate-400', soft: 'bg-slate-400/10' },
};

/** Texto extra que explica o estado de uma aula, quando houver. */
function lessonDetail(lesson) {
  if (lesson.status === 'substituicao' && lesson.scheduled_subject_name) {
    const titular = lesson.teacher_name ? ` — horário de ${lesson.teacher_name}` : '';
    return `no lugar de ${lesson.scheduled_subject_name}${titular}`;
  }
  if (lesson.status === 'nao_realizada' && lesson.not_held_reason) {
    return lesson.not_held_reason;
  }
  return null;
}

/**
 * Quem aparece na coluna Professor.
 *
 * O `teacher_name` da aula é o titular do horário na grade. Numa permuta,
 * quem deu a aula foi outra pessoa — e o diário não registra qual —, então
 * repetir o titular ao lado da outra disciplina se leria como se ele tivesse
 * dado a aula. Nesse caso o titular vai para o detalhe e a coluna fica vazia.
 */
function lessonTeacher(lesson) {
  if (lesson.status === 'substituicao') return '—';
  return lesson.teacher_name || '—';
}

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const WEEKDAY_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatBr(isoDate) {
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
}

/** Percentual formatado; null vira '—' (nada era esperado no período). */
function pct(value) {
  return value === null || value === undefined ? '—' : `${value}%`;
}

function rateColor(value) {
  if (value === null || value === undefined) return 'text-slate-400';
  if (value >= 90) return 'text-[#13983D]';
  if (value >= 70) return 'text-[#F9B047]';
  return 'text-[#E83A3C]';
}

function AcompanhamentoDiarios() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useTheme();

  const [month, setMonth] = useState(currentMonth());
  const [escolas, setEscolas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [idSchool, setIdSchool] = useState('');
  const [idClass, setIdClass] = useState('');

  const [overview, setOverview] = useState(null);
  const [pending, setPending] = useState(null);
  const [monthGrid, setMonthGrid] = useState(null);
  // id_class -> linha de /class-schedule/summary/ (grade vigente, não do mês)
  const [resumoGrade, setResumoGrade] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingPanel, setLoadingPanel] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('turmas');
  const [expandedDay, setExpandedDay] = useState(null);

  const card = darkMode
    ? 'bg-slate-800/50 border border-slate-700/10'
    : 'bg-white/80 border border-slate-200 shadow-sm';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const strong = darkMode ? 'text-slate-100' : 'text-slate-800';
  const inputCls = `px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
  }`;

  // Coordenador, supervisor e referência técnica acompanham a rede; o
  // professor vê o próprio preenchimento — o backend recorta as aulas pelas
  // disciplinas que ele leciona, não pela turma inteira.
  const canExport = [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.REFERENCIA_TECNICA]
    .includes(papelDeAcesso(user?.role));
  const isProfessor = user?.role === ROLES.PROFESSOR;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [escolasData, turmasData] = await Promise.all([
          fetchAllPaginated('/schools/').catch(() => []),
          fetchAllPaginated('/classes/').catch(() => []),
        ]);
        if (!alive) return;
        setEscolas(escolasData);
        setTurmas(turmasData);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const turmasFiltradas = useMemo(() => {
    if (!idSchool) return turmas;
    return turmas.filter(t => String(t.id_school) === String(idSchool));
  }, [turmas, idSchool]);

  const buildParams = useCallback(() => {
    const params = { month };
    if (idSchool) params.id_school = idSchool;
    if (idClass) params.id_class = idClass;
    return params;
  }, [month, idSchool, idClass]);

  const fetchPanels = useCallback(async () => {
    setLoadingPanel(true);
    setError(null);
    try {
      const params = buildParams();
      const [overviewData, pendingData, resumoData] = await Promise.all([
        apiGet('/diary-coverage/overview/', params),
        apiGet('/diary-coverage/pending/', params),
        // A situação da grade é um extra: se falhar, o painel some e o
        // acompanhamento continua. O professor não monta grade — não vê.
        isProfessor
          ? Promise.resolve(null)
          : apiGet('/class-schedule/summary/').catch((e) => {
              console.error('Erro ao carregar situação da grade:', e);
              return null;
            }),
      ]);
      setOverview(overviewData);
      setPending(pendingData);
      if (resumoData?.classes) {
        const mapa = {};
        resumoData.classes.forEach(l => { mapa[l.id_class] = l; });
        setResumoGrade(mapa);
      } else {
        setResumoGrade(null);
      }

      // A grade dia a dia só faz sentido com uma turma escolhida.
      if (idClass) {
        setMonthGrid(await apiGet('/diary-coverage/month/', params));
      } else {
        setMonthGrid(null);
      }
    } catch (err) {
      console.error('Erro ao carregar acompanhamento:', err);
      setError('Não foi possível carregar o acompanhamento dos diários.');
    } finally {
      setLoadingPanel(false);
    }
  }, [buildParams, idClass, isProfessor]);

  useEffect(() => { fetchPanels(); }, [fetchPanels]);

  useEffect(() => {
    if (!idClass && tab === 'calendario') setTab('turmas');
  }, [idClass, tab]);

  const summary = overview?.summary;

  const handleExport = () => {
    // A planilha sai com o mesmo recorte da tela — inclusive o do município
    // escolhido no menu, que a URL montada na mão não herdaria sozinha.
    const rota = '/diary-coverage/pending-export/';
    const params = new URLSearchParams(comCidadeAtiva(rota, buildParams())).toString();
    window.open(`${API_BASE_URL}${rota}?${params}`, '_blank');
  };

  const shiftMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Acompanhamento..."
        subtitle="Buscando escolas e turmas..."
        size="xl"
      />
    );
  }

  const [year, monthNum] = month.split('-').map(Number);

  return (
    <>
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
          }`}>
            Acompanhamento de Diários
          </h1>
          <p className={`text-sm mt-1 ${muted}`}>
            {isProfessor
              ? 'Suas aulas, uma a uma: o que você já lançou e o que está pendente.'
              : 'Aula por aula: o que já foi lançado e o que está pendente.'}
          </p>
        </div>
        {canExport && (
          <button
            onClick={handleExport}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25"
          >
            Exportar pendências (XLSX)
          </button>
        )}
      </header>

      {/* Filtros */}
      <div className={`rounded-xl p-4 mb-6 ${card}`}>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => shiftMonth(-1)}
              className={`px-2.5 py-2 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'}`}
              title="Mês anterior"
            >
              ‹
            </button>
            <div>
              <label className={`block text-xs font-medium mb-1 ${muted}`}>Mês</label>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className={inputCls}
              />
            </div>
            <button
              onClick={() => shiftMonth(1)}
              className={`px-2.5 py-2 rounded-lg mt-5 ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'}`}
              title="Próximo mês"
            >
              ›
            </button>
          </div>

          <div className="min-w-[220px]">
            <label className={`block text-xs font-medium mb-1 ${muted}`}>Escola</label>
            <SearchableSelect
              value={idSchool}
              onChange={(v) => { setIdSchool(v); setIdClass(''); }}
              options={escolas.map(e => ({ value: e.id, label: e.school_name || e.school }))}
              emptyOptionLabel="Todas as escolas"
              searchPlaceholder="Buscar escola..."
              darkMode={darkMode}
            />
          </div>

          <div className="min-w-[200px]">
            <label className={`block text-xs font-medium mb-1 ${muted}`}>Turma</label>
            <SearchableSelect
              value={idClass}
              onChange={setIdClass}
              options={turmasFiltradas.map(t => ({ value: t.id, label: t.class_name }))}
              emptyOptionLabel="Todas as turmas"
              searchPlaceholder="Buscar turma..."
              darkMode={darkMode}
            />
          </div>

          <button
            onClick={fetchPanels}
            disabled={loadingPanel}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            } ${loadingPanel ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {loadingPanel ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {error && (
        <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className="text-sm text-[#E83A3C]">{error}</p>
        </div>
      )}

      {/* Indicadores do mês */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className={`rounded-xl p-4 ${card}`}>
          <p className={`text-xs uppercase tracking-wider ${muted}`}>Aulas previstas</p>
          <p className={`text-2xl font-bold mt-1 ${strong}`}>{summary?.esperadas ?? '—'}</p>
          <p className={`text-[11px] mt-1 ${muted}`}>{MONTH_NAMES[monthNum - 1]} / {year}</p>
        </div>
        <div className={`rounded-xl p-4 ${card}`}>
          <p className={`text-xs uppercase tracking-wider ${muted}`}>Diários lançados</p>
          <p className="text-2xl font-bold mt-1 text-[#13983D]">{summary?.lancadas ?? '—'}</p>
          <p className={`text-[11px] mt-1 ${muted}`}>aula registrada + chamada</p>
        </div>
        <div className={`rounded-xl p-4 ${card}`}>
          <p className={`text-xs uppercase tracking-wider ${muted}`}>Sem frequência</p>
          <p className="text-2xl font-bold mt-1 text-[#F9B047]">{summary?.sem_frequencia ?? '—'}</p>
          <p className={`text-[11px] mt-1 ${muted}`}>aula registrada, falta a chamada</p>
        </div>
        <div className={`rounded-xl p-4 ${card}`}>
          <p className={`text-xs uppercase tracking-wider ${muted}`}>Não registradas</p>
          <p className="text-2xl font-bold mt-1 text-[#E83A3C]">{summary?.pendente ?? '—'}</p>
          <p className={`text-[11px] mt-1 ${muted}`}>
            {summary?.nao_realizada
              ? `${summary.nao_realizada} aula(s) não realizada(s) fora da cobrança`
              : 'nenhum registro na aula'}
          </p>
        </div>
        <div className={`rounded-xl p-4 ${card}`}>
          <p className={`text-xs uppercase tracking-wider ${muted}`}>Preenchimento</p>
          <p className={`text-2xl font-bold mt-1 ${rateColor(summary?.fill_rate)}`}>
            {pct(summary?.fill_rate)}
          </p>
          <p className={`text-[11px] mt-1 ${muted}`}>aulas futuras não contam</p>
        </div>
      </div>

      {resumoGrade && (
        <SituacaoGradePainel
          turmas={idClass
            ? turmasFiltradas.filter(t => String(t.id) === String(idClass))
            : turmasFiltradas}
          resumo={resumoGrade}
          darkMode={darkMode}
          onAbrirGrade={(id) => navigate(`/turmas/${id}/grade-horaria`)}
        />
      )}

      {/* Abas — quebram em duas linhas no celular, e "Por disciplina /
          professor" encolhe para "Disciplinas". Numa linha só, esse rótulo
          não cabia em 390px e esticava a página inteira para o lado. */}
      <div className={`flex flex-wrap gap-1 p-1 rounded-xl mb-4 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
        {[
          { id: 'turmas', label: 'Por turma', curto: 'Turmas' },
          { id: 'disciplinas', label: 'Por disciplina / professor', curto: 'Disciplinas' },
          { id: 'pendencias', label: `Pendências${pending ? ` (${pending.total})` : ''}` },
          { id: 'calendario', label: 'Dia a dia', disabled: !idClass },
        ].map(t => (
          <button
            key={t.id}
            disabled={t.disabled}
            onClick={() => setTab(t.id)}
            title={t.disabled ? 'Selecione uma turma para ver o dia a dia' : t.label}
            className={`flex-1 basis-[calc(50%-0.25rem)] sm:basis-0 min-w-0 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              t.disabled
                ? 'opacity-40 cursor-not-allowed'
                : tab === t.id
                ? 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] text-white shadow'
                : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-white'
            }`}
          >
            <span className={t.curto ? 'hidden sm:inline' : undefined}>{t.label}</span>
            {t.curto && <span className="sm:hidden">{t.curto}</span>}
          </button>
        ))}
      </div>

      {/* Por turma */}
      {tab === 'turmas' && (
        <div className={`rounded-xl overflow-hidden ${card}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}>
                <tr className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                  <th className="text-left px-4 py-3">Turma</th>
                  <th className="text-left px-4 py-3">Escola</th>
                  <th className="text-center px-4 py-3">Previstas</th>
                  <th className="text-center px-4 py-3">Lançadas</th>
                  <th className="text-center px-4 py-3">Sem freq.</th>
                  <th className="text-center px-4 py-3">Pendentes</th>
                  <th className="text-center px-4 py-3">Preenchimento</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                {(overview?.classes || []).map(c => (
                  <tr key={c.id_class} className={darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}>
                    <td className={`px-4 py-3 font-medium ${strong}`}>{c.class_name}</td>
                    <td className={`px-4 py-3 ${muted}`}>{c.school_name || '—'}</td>
                    <td className={`px-4 py-3 text-center ${strong}`}>{c.summary.esperadas}</td>
                    <td className="px-4 py-3 text-center text-[#13983D] font-semibold">{c.summary.lancadas}</td>
                    <td className="px-4 py-3 text-center text-[#F9B047] font-semibold">{c.summary.sem_frequencia}</td>
                    <td className="px-4 py-3 text-center text-[#E83A3C] font-semibold">{c.summary.pendente}</td>
                    <td className={`px-4 py-3 text-center font-bold ${rateColor(c.summary.fill_rate)}`}>
                      {pct(c.summary.fill_rate)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setIdClass(String(c.id_class)); setTab('calendario'); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium mr-2 ${darkMode
                          ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                      >
                        Dia a dia
                      </button>
                      <button
                        onClick={() => navigate(`/turmas/${c.id_class}/diario`)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0974BA]/15 text-[#0974BA] hover:bg-[#0974BA]/25"
                      >
                        Diário
                      </button>
                    </td>
                  </tr>
                ))}
                {(!overview?.classes || overview.classes.length === 0) && (
                  <tr>
                    <td colSpan={8} className={`px-4 py-10 text-center ${muted}`}>
                      Nenhuma turma com grade horária no período selecionado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Por disciplina / professor */}
      {tab === 'disciplinas' && (
        <div className={`rounded-xl overflow-hidden ${card}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}>
                <tr className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                  <th className="text-left px-4 py-3">Turma</th>
                  <th className="text-left px-4 py-3">Disciplina</th>
                  <th className="text-left px-4 py-3">Professor</th>
                  <th className="text-center px-4 py-3">Previstas</th>
                  <th className="text-center px-4 py-3">Lançadas</th>
                  <th className="text-center px-4 py-3">Pendentes</th>
                  <th className="text-center px-4 py-3">Preenchimento</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                {(overview?.subjects || []).map(s => (
                  <tr
                    key={`${s.id_class}-${s.id_subject}`}
                    className={darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}
                  >
                    <td className={`px-4 py-3 ${strong}`}>{s.class_name}</td>
                    <td className={`px-4 py-3 font-medium ${strong}`}>{s.subject_name || '—'}</td>
                    <td className={`px-4 py-3 ${muted}`}>{s.teacher_name || 'Sem professor na grade'}</td>
                    <td className={`px-4 py-3 text-center ${strong}`}>{s.summary.esperadas}</td>
                    <td className="px-4 py-3 text-center text-[#13983D] font-semibold">{s.summary.lancadas}</td>
                    <td className="px-4 py-3 text-center text-[#E83A3C] font-semibold">
                      {s.summary.pendente + s.summary.sem_frequencia}
                    </td>
                    <td className={`px-4 py-3 text-center font-bold ${rateColor(s.summary.fill_rate)}`}>
                      {pct(s.summary.fill_rate)}
                    </td>
                  </tr>
                ))}
                {(!overview?.subjects || overview.subjects.length === 0) && (
                  <tr>
                    <td colSpan={7} className={`px-4 py-10 text-center ${muted}`}>
                      Nada a exibir no período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pendências */}
      {tab === 'pendencias' && (
        <div className={`rounded-xl overflow-hidden ${card}`}>
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className={`sticky top-0 ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                <tr className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Turma</th>
                  <th className="text-center px-4 py-3">Aula</th>
                  <th className="text-left px-4 py-3">Disciplina</th>
                  <th className="text-left px-4 py-3">Professor</th>
                  <th className="text-left px-4 py-3">Situação</th>
                  <th className="text-right px-4 py-3">Ação</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                {(pending?.results || []).map((l, i) => {
                  const meta = STATUS_META[l.status];
                  return (
                    <tr key={`${l.date}-${l.id_class}-${l.period}-${i}`} className={darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}>
                      <td className={`px-4 py-3 whitespace-nowrap ${strong}`}>
                        {formatBr(l.date)}
                        <span className={`ml-2 text-xs ${muted}`}>{WEEKDAY_SHORT[l.weekday]}</span>
                      </td>
                      <td className={`px-4 py-3 ${strong}`}>{l.class_name}</td>
                      <td className={`px-4 py-3 text-center ${muted}`}>
                        {l.period ? `${l.period}ª` : '—'}
                        {l.time_label && <div className="text-[11px]">{l.time_label}</div>}
                      </td>
                      <td className={`px-4 py-3 ${strong}`}>
                        {l.subject_name || '—'}
                        {lessonDetail(l) && (
                          <div className={`text-[11px] font-normal ${muted}`}>{lessonDetail(l)}</div>
                        )}
                      </td>
                      <td className={`px-4 py-3 ${muted}`}>{lessonTeacher(l)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium ${meta.soft} ${meta.text}`}>
                          <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(`/turmas/${l.id_class}/diario`)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0974BA]/15 text-[#0974BA] hover:bg-[#0974BA]/25"
                        >
                          Abrir diário
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {pending && pending.results.length === 0 && (
                  <tr>
                    <td colSpan={7} className={`px-4 py-12 text-center ${muted}`}>
                      <div className="text-3xl mb-2">✅</div>
                      Nenhuma pendência no período. Todos os diários em dia.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dia a dia */}
      {tab === 'calendario' && monthGrid && (
        <div className={`rounded-xl p-5 ${card}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className={`text-sm font-semibold uppercase tracking-wider ${muted}`}>
              {MONTH_NAMES[monthNum - 1]} / {year} — dia a dia
            </h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <span key={key} className={`inline-flex items-center gap-1.5 text-xs ${muted}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                  {meta.label}
                </span>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            {monthGrid.days
              .filter(d => d.lessons.length > 0)
              .map(day => {
                const aberto = expandedDay === day.date;
                return (
                  <div
                    key={day.date}
                    className={`rounded-lg border ${darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white'}`}
                  >
                    <button
                      onClick={() => setExpandedDay(aberto ? null : day.date)}
                      className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`text-sm font-semibold ${strong}`}>{formatBr(day.date)}</span>
                        <span className={`text-xs ${muted}`}>{WEEKDAY_SHORT[day.weekday]}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-1 justify-end flex-wrap">
                        {day.lessons.map((l, i) => {
                          const meta = STATUS_META[l.status];
                          return (
                            <span
                              key={`${l.period}-${i}`}
                              title={[
                                l.period ? `${l.period}ª aula` : 'Aula extra',
                                l.subject_name || '—',
                                meta.label,
                                lessonDetail(l),
                              ].filter(Boolean).join(' • ')}
                              className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-bold text-white ${meta.dot}`}
                            >
                              {l.period || '+'}
                            </span>
                          );
                        })}
                        <span className={`ml-3 text-sm font-bold ${rateColor(day.summary.fill_rate)}`}>
                          {pct(day.summary.fill_rate)}
                        </span>
                      </div>
                    </button>

                    {aberto && (
                      <div className={`px-4 pb-3 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                        <table className="w-full text-xs mt-2">
                          <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                            {day.lessons.map((l, i) => {
                              const meta = STATUS_META[l.status];
                              return (
                                <tr key={`${l.period}-det-${i}`}>
                                  <td className={`py-2 pr-3 w-16 ${muted}`}>
                                    {l.period ? `${l.period}ª aula` : 'Extra'}
                                  </td>
                                  <td className={`py-2 pr-3 w-28 ${muted}`}>{l.time_label || '—'}</td>
                                  <td className={`py-2 pr-3 font-medium ${strong}`}>
                                    {l.subject_name || '—'}
                                    {lessonDetail(l) && (
                                      <div className={`font-normal ${muted}`}>{lessonDetail(l)}</div>
                                    )}
                                  </td>
                                  <td className={`py-2 pr-3 ${muted}`}>{lessonTeacher(l)}</td>
                                  <td className="py-2 text-right">
                                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium ${meta.soft} ${meta.text}`}>
                                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                      {meta.label}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}

            {monthGrid.days.every(d => d.lessons.length === 0) && (
              <div className={`py-12 text-center ${muted}`}>
                Nenhuma aula prevista neste mês. Verifique a grade horária da turma.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default AcompanhamentoDiarios;
